import {deserializeV2, fetchAndDeserialize, serialize} from '../../util/api-adapter';
import {FetchMethodEnum, fetchX} from '../../util/fetch';
import {objectToUrlParameters} from '../../util/url';
import {getCsrfHeaders, mainApiHeaders, rootApiUrl} from '../api/api-const';

import {
    AiMonitoringAnswersParametersType,
    aiMonitoringAnswersSchema,
    AiMonitoringAnswersType,
    aiMonitoringAvailableCompetitorsSchema,
    AiMonitoringAvailableCompetitorType,
    aiMonitoringBrandAnalysisSchema,
    AiMonitoringBrandAnalysisType,
    aiMonitoringConfigSchema,
    AiMonitoringConfigType,
    AiMonitoringConfigUpdatePayloadType,
    aiMonitoringRecommendationsSchema,
    AiMonitoringRecommendationsType,
    aiMonitoringRunSchema,
    aiMonitoringRunStartSchema,
    AiMonitoringRunStartType,
    AiMonitoringRunType,
    AiMonitoringScoresParametersType,
    aiMonitoringScoresSchema,
    AiMonitoringScoresType,
} from './ai-brand-monitoring-type';

function getBasePath(brandId: number): string {
    return `/cp/brands/${brandId}/ai-monitoring`;
}

export function fetchAiMonitoringConfig(brandId: number): Promise<AiMonitoringConfigType> {
    return fetchAndDeserialize<AiMonitoringConfigType>(`${getBasePath(brandId)}/config/`, aiMonitoringConfigSchema);
}

export async function updateAiMonitoringConfig(
    brandId: number,
    payload: AiMonitoringConfigUpdatePayloadType
): Promise<AiMonitoringConfigType> {
    const url = `${rootApiUrl}${getBasePath(brandId)}/config/`;

    const result = await fetchX(url, {
        method: FetchMethodEnum.put,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        body: JSON.stringify(serialize(payload)),
    });

    return deserializeV2<AiMonitoringConfigType>(url, aiMonitoringConfigSchema, result);
}

export function fetchAiMonitoringAvailableCompetitors(
    brandId: number
): Promise<Array<AiMonitoringAvailableCompetitorType>> {
    return fetchAndDeserialize<Array<AiMonitoringAvailableCompetitorType>>(
        `${getBasePath(brandId)}/available-competitors/`,
        aiMonitoringAvailableCompetitorsSchema
    );
}

export async function startAiMonitoringRun(brandId: number): Promise<AiMonitoringRunStartType> {
    const url = `${rootApiUrl}${getBasePath(brandId)}/runs/`;

    const result = await fetchX(url, {
        method: FetchMethodEnum.post,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        body: JSON.stringify({}),
    });

    return deserializeV2<AiMonitoringRunStartType>(url, aiMonitoringRunStartSchema, result);
}

export function fetchAiMonitoringRun(brandId: number, runId: string): Promise<AiMonitoringRunType> {
    return fetchAndDeserialize<AiMonitoringRunType>(`${getBasePath(brandId)}/runs/${runId}/`, aiMonitoringRunSchema);
}

export function fetchAiMonitoringBrandAnalysis(brandId: number, runId: string): Promise<AiMonitoringBrandAnalysisType> {
    const queryString = objectToUrlParameters({run_id: runId});

    return fetchAndDeserialize<AiMonitoringBrandAnalysisType>(
        `${getBasePath(brandId)}/analysis/?${queryString}`,
        aiMonitoringBrandAnalysisSchema
    );
}

export function fetchAiMonitoringScores(
    brandId: number,
    parameters: AiMonitoringScoresParametersType
): Promise<AiMonitoringScoresType> {
    const queryString = objectToUrlParameters({
        date_from: parameters.dateFrom,
        date_to: parameters.dateTo,
        granularity: parameters.granularity ?? 'day',
    });

    return fetchAndDeserialize<AiMonitoringScoresType>(
        `${getBasePath(brandId)}/scores/?${queryString}`,
        aiMonitoringScoresSchema
    );
}

export function fetchAiMonitoringAnswers(
    brandId: number,
    parameters: AiMonitoringAnswersParametersType
): Promise<AiMonitoringAnswersType> {
    const queryString = objectToUrlParameters({
        run_id: parameters.runId,
        prompt_id: parameters.promptId,
        page: parameters.page,
        page_size: parameters.pageSize,
    });

    return fetchAndDeserialize<AiMonitoringAnswersType>(
        `${getBasePath(brandId)}/answers/?${queryString}`,
        aiMonitoringAnswersSchema
    );
}

export function fetchAiMonitoringRecommendations(
    brandId: number,
    runId: string
): Promise<AiMonitoringRecommendationsType> {
    const queryString = objectToUrlParameters({run_id: runId});

    return fetchAndDeserialize<AiMonitoringRecommendationsType>(
        `${getBasePath(brandId)}/recommendations/?${queryString}`,
        aiMonitoringRecommendationsSchema
    );
}
